import React from "react";
import { View, StyleSheet, StyleProp, ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons, FontAwesome5 } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";

type ChipType = "date" | "time" | "duration" | "earnings" | "status" | "alert";

interface TimeChipProps {
  type: ChipType;
  text: string;
  icon?: keyof typeof MaterialCommunityIcons.glyphMap | keyof typeof FontAwesome5.glyphMap;
  style?: StyleProp<ViewStyle>;
  size?: "small" | "medium";
  IconComponent?: typeof MaterialCommunityIcons | typeof FontAwesome5;
}

export function TimeChip({
  type,
  text,
  icon,
  style,
  size = "medium",
  IconComponent = MaterialCommunityIcons,
}: TimeChipProps) {
  const { colors } = useTheme();

  const typeStyles = {
    date: {
      backgroundColor: colors.backgroundSecondary,
      borderColor: colors.border,
      textColor: colors.text,
      defaultIcon: "calendar" as keyof typeof MaterialCommunityIcons.glyphMap,
    },
    time: {
      backgroundColor: colors.backgroundSecondary,
      borderColor: colors.border,
      textColor: colors.text,
      defaultIcon: "clock-outline" as keyof typeof MaterialCommunityIcons.glyphMap,
    },
    duration: {
      backgroundColor: colors.blue?.border || "#3b82f6cc",
      borderColor: colors.blue?.border || "#3b82f6",
      textColor: "#ffffff",
      defaultIcon: "timelapse" as keyof typeof MaterialCommunityIcons.glyphMap,
    },
    earnings: {
      backgroundColor: colors.emerald?.border || "#10b981cc",
      borderColor: colors.emerald?.border || "#10b981",
      textColor: "#ffffff",
      defaultIcon: "currency-usd" as keyof typeof MaterialCommunityIcons.glyphMap,
    },
    status: {
      backgroundColor: colors.amber?.border || "#f59e0bcc",
      borderColor: colors.amber?.border || "#f59e0b",
      textColor: "#ffffff",
      defaultIcon: "information-outline" as keyof typeof MaterialCommunityIcons.glyphMap,
    },
    alert: {
      backgroundColor: colors.dangerBackground || "#f59e0bcc",
      borderColor: colors.dangerBorder || "#f59e0b",
      textColor: colors.dangerText || "#ffffff",
      defaultIcon: "account-cancel-outline" as keyof typeof MaterialCommunityIcons.glyphMap,
    },
  };

  const sizeStyles = {
    small: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      fontSize: 11,
      iconSize: 10,
    },
    medium: {
      paddingHorizontal: 8,
      paddingVertical: 4,
      fontSize: 12,
      iconSize: 12,
    },
  };

  const type_style = typeStyles[type] || typeStyles.status; // Fallback to status if type not found
  const size_style = sizeStyles[size] || sizeStyles.medium; // Fallback to medium if size not found
  const displayIcon = icon || type_style.defaultIcon;

  return (
    <View
      style={[
        styles.chip,
        {
          backgroundColor: type_style.backgroundColor,
          borderColor: type_style.borderColor,
          paddingHorizontal: size_style.paddingHorizontal,
          paddingVertical: size_style.paddingVertical,
        },
        style,
      ]}
    >
      <IconComponent
        name={displayIcon as any}
        size={size_style.iconSize}
        color={type_style.textColor}
        style={{ marginRight: 4 }}
      />
      <ThemedText
        style={[
          styles.chipText,
          {
            color: type_style.textColor,
            fontSize: size_style.fontSize,
          },
        ]}
      >
        {text}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  chip: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    alignSelf: "flex-start",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.1)",
    elevation: 1,
  },
  chipText: {
    fontWeight: "700",
    letterSpacing: 0.3,
  },
});
